import { Injectable } from '@angular/core';
import {
    AircraftType,
    Airport,
    CarrierType,
    FareComponentInBookingType,
    FareFamilyType,
    JourneyInOfferType,
    JourneyInTripType,
    LocationForAirType,
    OfferType,
    OriginDestinationInOfferType,
    OriginDestinationInTripType,
    OriginDestinationXRefType,
    PersonNameType,
    PriceDetailInBookingType,
    PriceEnvelopeInOfferType,
    PriceInOfferType,
    PriceItemType,
    PriceTotalsContentInBookingType,
    ProductInTripType,
    PropertyXRefType,
    RateInBookingType,
    ResponseForConvertOfferAfx2jsonType,
    RuleType,
    SectorCommonType,
    SectorInOfferType,
    SectorInTripType,
    SectorXRefType,
    StopType,
    TransportationInTripType,
    TravellerXRefType,
    TypeForRateType,
    VendorType
} from "@afidium/ts-facade";
import {
    EFareType,
    IAirTripQuoteCurrency, IBookingCarbonFootprint,
    IPercentage,
    IQuote,
    IQuoteExchange,
    IQuoteExchangeRule,
    IQuoteExchangeRuleValidity,
    IQuoteLuggage,
    IRestriction,
    IRule,
    ISector,
    ISegments,
    ITravellers,
    SectorXCategory
} from "libraries/service/resources/airTrip/IGetAirTripQuotes";
import { IFareFamily, IPrice, IPriceDetail, IPriceDetails, IPriceTotal } from '../booking/interfaces/IGetBookings';
import { EXRateType, ITotal } from "libraries/interfaces/ITotal";
import * as moment from "moment";
import { RestrictionUnit } from "libraries/service/resources/model/booking/air/IAirPrice";
import { ICodeName } from "libraries/service/resources/model/shared/ICodeName";
import { Yii } from "libraries/interfaces/Yii";
import { EInterfaceValues } from "libraries/component/interface-flag/interface-flag.component";
import { PenaltyType } from "@afidium/ts-facade/model/penaltyType";
import _isEqual from "lodash-es/isEqual";

interface IMinutesDuration {
    minutes: number;
}

interface ISeat {
    max?: number;
    status?: string;
    xCabin?: string;
}

interface ISeats {
    [key: string]: ISeat;
}

export enum EUsedXReason {
    all = "all",
    TransportationPlusPayment = "TransportationPlusPayment",
    Service = "Service"
}

/**
 * Use to transform New GQA format into Old GQA Format
 * in order to be used by regular components
 */
@Injectable()
export class AirQuoteMappingService {

    /**
     * temporary Partial, WIP
     */
    public mapGqaNewToOld(newGqa: ResponseForConvertOfferAfx2jsonType, offerAlternativePriceXId?: string, priceXReason: EUsedXReason = EUsedXReason.all): Partial<IQuote> {

        const transactionInformation: Partial<IQuote> = this.mapTransactionInformation(newGqa);
        const segment: ISegments = this.mapSegment(newGqa, offerAlternativePriceXId);
        const price: IPrice[] = this.mapRootPrice(newGqa, offerAlternativePriceXId, priceXReason);
        const total: ITotal[] = this.mapRootTotal(newGqa, offerAlternativePriceXId, priceXReason);
        const traveller: ITravellers = this.mapTravellers(newGqa);
        const luggage: IQuoteLuggage = this.mapLuggages(newGqa, offerAlternativePriceXId);
        const fareFamily: { [key: string]: IFareFamily } = this.extractFareFamily(newGqa, offerAlternativePriceXId);
        const fareType: EFareType[] = this.extractFareType(newGqa, offerAlternativePriceXId);
        const vendor: ICodeName = this.extractVendor(newGqa, offerAlternativePriceXId);
        const exchange: IQuoteExchange = this.extractExchangeOrCancellation(newGqa, 'exchange', offerAlternativePriceXId);
        const cancellation: IQuoteExchange = this.extractExchangeOrCancellation(newGqa, 'cancellation', offerAlternativePriceXId);
        const carbonFootprintArray: IBookingCarbonFootprint[] = this.extractCarbonFootprintArray(newGqa, offerAlternativePriceXId);
        const currency: IAirTripQuoteCurrency = {
            code: newGqa.currencyArray[0].currencyCode,
            decimals: newGqa.currencyArray[0].decimals,
        };
        const interfaceValue: EInterfaceValues = this.extractInterface(newGqa, offerAlternativePriceXId);

        return {
            ...transactionInformation,
            vendor,
            fareType,
            segment,
            price,
            total,
            traveller,
            luggage,
            fareFamily,
            fareFamilyDictionary: newGqa.dataList?.fareFamilyArray,
            exchange,
            cancellation,
            currency,
            carbonFootprintArray,
            interface: interfaceValue,
        };
    }

    public extractExchangeOrCancellation(newGqa: ResponseForConvertOfferAfx2jsonType, type: string, offerAlternativePriceXId?: string): IQuoteExchange {
        const relatedOffer: OfferType = this.getOfferAlternativeWithPriceXId(newGqa, offerAlternativePriceXId);
        const relatedRule: RuleType[] = (this.getAllRulesFromOffer(relatedOffer) || [])
            .filter((rule: RuleType): boolean => {
                return rule.xRole.toLowerCase() === type.toLowerCase();
            });

        if (relatedRule?.length) {
            const rulesList: RuleType[] = relatedRule.filter((item: RuleType): boolean => Boolean(item.penaltyArray?.length));

            //make an array with all penalties froms all rules
            const penaltyArray: PenaltyType[] = rulesList
                .flatMap((ruleItem: RuleType): PenaltyType[] => ruleItem.penaltyArray);

            //if no penaties, go away
            if(!penaltyArray?.length){
                return null;
            }

            //extract simple rules from penalties
            const rule: IQuoteExchangeRule[] = penaltyArray
                .map((penaltyItem: PenaltyType): IQuoteExchangeRule => {
                    const penalty: number | IPercentage = penaltyItem.currency && penaltyItem.currency.amount !== ".00" ?
                        penaltyItem.currency.value :
                        penaltyItem.percentage ?
                            {
                                percentage: penaltyItem.percentage,
                            } : null;
                    // @ts-ignore
                    const validity: IQuoteExchangeRuleValidity = penaltyItem.xCountingFrom ? {
                        countingFrom: penaltyItem.xCountingFrom,
                        role: penaltyItem.xRole,
                    } : null;

                    return {
                        penalty,
                        validity,
                    };
                })
                .filter((item: IQuoteExchangeRule): boolean => Boolean(item.penalty));

            //remove identical rules
            const unDupes: IQuoteExchangeRule[] = [];
            rule.forEach((item: IQuoteExchangeRule): void => {
                const alreadyExist: IQuoteExchangeRule = unDupes.find((unDupe: IQuoteExchangeRule): boolean => _isEqual(unDupe, item));
                if (!alreadyExist) {
                    unDupes.push(item);
                }
            });

            return unDupes.length ? {rule: unDupes} : null;
        }
        return null;
    }

    public mapTransactionInformation(newGqa: ResponseForConvertOfferAfx2jsonType): Partial<IQuote> {
        return {
            transactionId: newGqa.transaction.transactionId,
            host: newGqa.transaction.hostArray[0].code,
            actionArray: newGqa.transaction.actionArray,
        };
    }

    public mapSegment(newGqa: ResponseForConvertOfferAfx2jsonType, offerAlternativePriceXId?: string): ISegments {

        const segment: ISegments = {};

        const product: ProductInTripType = newGqa.trip.productArray
            //EJ : do we need to filter on xIs ? I think yes
            .filter(
                (productItem: ProductInTripType): boolean => productItem.xIs === 'Transportation'
            )[0];
        product
            .originDestinationArray
            .forEach(
                (originDestination: OriginDestinationXRefType): void => {

                    const relatedOriginDestination: OriginDestinationInTripType = this.getOriginDestinationWithXref(originDestination.xRef, newGqa);

                    const journey: string[] = this.mapXrefToStringArray(relatedOriginDestination.journeyArray as PropertyXRefType[]);
                    // @ts-ignore
                    segment[relatedOriginDestination.xId] = {
                        xId: relatedOriginDestination.xId,
                        journey
                    };

                    journey.forEach((journeyXRef: string): void => {

                        const relatedJourneyInOffer: JourneyInOfferType | null = this.getJourneyInOffer(journeyXRef, newGqa, offerAlternativePriceXId);

                        const relatedJourney: JourneyInTripType = this.getJourneyWithXref(journeyXRef, newGqa);

                        const sector: ISector[] =
                            this.mapSector(relatedJourney.sectorArray, newGqa, journeyXRef, offerAlternativePriceXId);


                        segment[relatedOriginDestination.xId][relatedJourney.xId] = {
                            // duration ???
                            sector,
                            syscode: relatedJourneyInOffer?.syscode?.value,
                            stock: relatedJourney.stock,
                            //je pense qu'on se fiche du reste des infos, uniquement présentes car dans SQA pour filtres
                            begin: sector[0].begin,
                            from: sector[0].from,
                            end: sector[sector.length - 1].end,
                            to: sector[sector.length - 1].to,
                            carrier: this.extractCarrierCodesFromSectors(sector),
                            // duration: this.computeSegmentDuration(sector),
                            duration: relatedJourney.duration?.minute ? {
                                minutes: relatedJourney.duration.minute
                            } : null,
                            durationStop: this.computeJourneyDurationStop(relatedJourney),
                            stop: relatedJourney.stopQuantity ? relatedJourney.stopQuantity : 0,
                            carbonFootprint: relatedJourneyInOffer?.carbonFootprintArray ? relatedJourneyInOffer.carbonFootprintArray[0] : null,
                        };
                    });
                }
            );
        return segment;
    }

    public computeJourneyDurationStop(journey: JourneyInTripType): { minutes: number } {
        const minutes: number = journey.stopArray?.length ?
            journey.stopArray.reduce(
                (minutesTotal: number, current: StopType): number => {
                    return minutesTotal + (current.duration?.minute ? current.duration.minute : 0);
                },
                0
            ) : null;

        return minutes ? {
            minutes
        } : null;
    }

    public getJourneyInOffer(journeyXRef: string, newGqa: ResponseForConvertOfferAfx2jsonType, offerAlternativePriceXId?: string): JourneyInOfferType | null {
        if (!offerAlternativePriceXId) {
            return newGqa.offer.journeyArray.find((journeyXRefItem: JourneyInOfferType): boolean => journeyXRefItem.xRef === journeyXRef);
        }
        const relatedOffer: OfferType = this.getOfferAlternativeWithPriceXId(newGqa, offerAlternativePriceXId);
        return relatedOffer.journeyArray.find((journeyXRefItem: JourneyInOfferType): boolean => journeyXRefItem.xRef === journeyXRef);
    }

    public mapSector(sectorXRefArray: SectorXRefType[], newGqa: ResponseForConvertOfferAfx2jsonType, journeyXref: string, offerAlternativePriceXId?: string): ISector[] {

        if (!sectorXRefArray) {
            console.error(
                Yii.t('wui-iqba-js', 'sectorArray est manquant dans trip.journeyArray'),
                {journeyXref, sectorXRefArray});
        }

        const sectors: ISector[] = sectorXRefArray
            .map((xRef: SectorXRefType): SectorInTripType => this.getSectorWithXref(xRef.xRef, newGqa))
            .map((sector: SectorCommonType): ISector => {

                const operatedBy: { carrier: ICodeName } = sector.operatingCarrier?.afid ?
                    {
                        carrier: {
                            code: this.getCarrierCodeFromAfid(sector.operatingCarrier.afid, newGqa),
                        }
                    } :
                    null;
                const airCraftString = sector.aircraft && sector.aircraft?.afid !== null ? this.getAircraftCodeFromAfid(sector.aircraft.afid, newGqa) : null;
                return {
                    carbonFootprint: sector.carbonFootprintArray ? sector.carbonFootprintArray[0] : null,
                    xId: sector.xId,
                    begin: sector.begin?.value,
                    end: sector.end?.value,
                    duration: sector.duration?.minute ? {
                        minutes: sector.duration.minute
                    } : null,
                    from: this.getAirportCodeFromAfid(sector.from.afid, newGqa),
                    // fromTerminal ??
                    to: this.getAirportCodeFromAfid(sector.to.afid, newGqa),
                    // toTerminal ??
                    carrier: {
                        code: this.getCarrierCodeFromAfid(sector.carrier.afid, newGqa)
                    },
                    flight: sector.flight,
                    xCategory: sector.xCategory.toLowerCase() as unknown as SectorXCategory,
                    aircraft: airCraftString,
                    seat: this.extractAndMapSectorSeat(journeyXref, sector.xId, newGqa, offerAlternativePriceXId),
                    operatedBy,
                };
            });

        //compute durationStop
        sectors.map(
            (sector: ISector, index: number): ISector => {
                if (sectors[index + 1]) {
                    const stopBegin: moment.Moment = moment(sector.end);
                    const stopEnd: moment.Moment = moment(sectors[index + 1].begin);
                    sector.durationStop = {
                        minutes: stopEnd.diff(stopBegin, 'minutes')
                    };
                }
                return sector;
            }
        );

        return sectors;
    }

    public extractAndMapSectorSeat(
        journeyXId: string,
        sectorXId: string,
        newGqa: ResponseForConvertOfferAfx2jsonType,
        offerAlternativePriceXId?: string): ISeats {

        const relatedOffer: OfferType = this.getOfferAlternativeWithPriceXId(newGqa, offerAlternativePriceXId);

        const relatedOfferJourney: JourneyInOfferType = relatedOffer.journeyArray
            .find((offerJourneyItem: JourneyInOfferType): boolean => offerJourneyItem.xRef === journeyXId);

        if (!relatedOfferJourney) {
            return null;
        }

        const relatedOfferSector: SectorInOfferType = relatedOffer.sectorArray
            .find((sectorItem: SectorInOfferType): boolean => sectorItem.xRef === sectorXId);

        if (!relatedOfferSector?.seat) {
            return null;
        }

        return {
            [relatedOfferSector.seat.code]: {
                xCabin: relatedOfferSector.seat.xCabin
            }
        };
    }

    public extractFareType(newGqa: ResponseForConvertOfferAfx2jsonType, offerAlternativePriceXId?: string, selectPrice: EXRateType = EXRateType.cost): EFareType[] {
        const relatedOffer: OfferType = this.getOfferAlternativeWithPriceXId(newGqa, offerAlternativePriceXId);
        const prices: PriceInOfferType[] = relatedOffer.priceArray
            .filter((item: PriceInOfferType): boolean => Boolean(this.getCostOrRetail(item, selectPrice)));
        const rate: RateInBookingType = this.getCostOrRetail(prices[0], selectPrice).detailArray[0].rate;
        if (rate?.typeArray?.length) {
            return rate.typeArray
                .filter((type: TypeForRateType): boolean => Boolean(type.xRole))
                .map((type: TypeForRateType): EFareType => type.xRole as EFareType);
        }
        return null;
    }

    public extractCarbonFootprintArray(newGqa: ResponseForConvertOfferAfx2jsonType, offerAlternativePriceXId?: string): IBookingCarbonFootprint[] {
        const relatedOffer: OfferType = this.getOfferAlternativeWithPriceXId(newGqa, offerAlternativePriceXId);
        return relatedOffer?.carbonFootprintArray ? [{carbonFootprint: relatedOffer.carbonFootprintArray[0]}] : null;
    }

    public extractVendor(newGqa: ResponseForConvertOfferAfx2jsonType, offerAlternativePriceXId?: string, selectPrice: EXRateType = EXRateType.cost): ICodeName {
        const relatedOffer: OfferType = this.getOfferAlternativeWithPriceXId(newGqa, offerAlternativePriceXId);
        const prices: PriceInOfferType[] = relatedOffer.priceArray
            .filter((item: PriceInOfferType): boolean => {
                const itemPrice = this.getCostOrRetail(item, selectPrice);
                return itemPrice && itemPrice.xRole === 'Transportation';
            });
        const vendor: VendorType = this.getCostOrRetail(prices[0], selectPrice).detailArray[0].rate?.vendor;

        return Boolean(vendor) ?
            {
                code: vendor.iata,
                name: vendor.name?.value,
            } :
            null;
    }

    public extractInterface(newGqa: ResponseForConvertOfferAfx2jsonType, offerAlternativePriceXId?: string, selectPrice: EXRateType = EXRateType.cost): EInterfaceValues {
        const relatedOffer: OfferType = this.getOfferAlternativeWithPriceXId(newGqa, offerAlternativePriceXId);
        // @ts-ignore
        return relatedOffer.host.interface?.name;
    }

    public extractFareFamily(newGqa: ResponseForConvertOfferAfx2jsonType, offerAlternativePriceXId?: string): {
        [key: string]: IFareFamily
    } {
        const relatedOffer: OfferType = this.getOfferAlternativeWithPriceXId(newGqa, offerAlternativePriceXId);

        const fareComponentArray: FareComponentInBookingType[] = this.getAllFareComponentArrayFromOffer(relatedOffer);

        const fareFamilyCollection: { [key: string]: IFareFamily } = {};
        if (relatedOffer.originDestinationArray?.length && fareComponentArray?.length) {
            fareComponentArray
                .filter((fareComponent: FareComponentInBookingType): boolean => Boolean(fareComponent.fareFamily && fareComponent.sectorArray?.length))
                .forEach(
                    (fareComponent: FareComponentInBookingType): void => {
                        const fareFamily: FareFamilyType = fareComponent.fareFamily;
                        const segmentArray: string[] = fareComponent.sectorArray
                            .map((service: SectorXRefType): string => service.xRef)
                            .map(
                                (xRef: string): string => relatedOffer.originDestinationArray
                                    .find((originDestination: OriginDestinationInOfferType): boolean => {
                                        return Boolean(originDestination.sectorArray.find((sectorRef: SectorXRefType): boolean => sectorRef.xRef === xRef));
                                    })
                                    .xRef
                            );

                        const sectorArray: string[] = fareComponent.sectorArray
                            .map((service: SectorXRefType): string => service.xRef)
                            .filter(
                                (xRef: string): OriginDestinationInOfferType => relatedOffer.originDestinationArray
                                    .find((originDestination: OriginDestinationInOfferType): boolean => {
                                        return Boolean(originDestination.sectorArray.find((sectorRef: SectorXRefType): boolean => sectorRef.xRef === xRef));
                                    })
                            ).map((serviceId: string) => relatedOffer.sectorArray.find((sector: SectorInOfferType) => sector.xId === serviceId).xRef);

                        if (!fareFamilyCollection[fareFamily.xRef]) {
                            const name: string = fareFamily.name ?
                                fareFamily.name.value :
                                fareFamily.code;
                            fareFamilyCollection[fareFamily.xRef] = {
                                xId: fareFamily.xRef,
                                code: fareFamily.code,
                                syscode: fareFamily.syscode.value,
                                name,
                                segment: Array.from(new Set<string>(segmentArray)),// we want unique values
                                sector: Array.from(new Set<string>(sectorArray))
                            };
                        } else {
                            const segmentToAdd: string[] = [];
                            segmentArray.forEach((id: string): void => {
                                if (!fareFamilyCollection[fareFamily.xRef].segment.includes(id)) {
                                    segmentToAdd.push(id);
                                }
                            });

                            const sectorToAdd: string[] = [];
                            sectorArray.forEach((id: string): void => {
                                if (!fareFamilyCollection[fareFamily.xRef].sector.includes(id)) {
                                    sectorToAdd.push(id);
                                }
                            });

                            fareFamilyCollection[fareFamily.xRef] = {
                                ...fareFamilyCollection[fareFamily.xRef],
                                segment:
                                    Array.from(new Set<string>([
                                        ...fareFamilyCollection[fareFamily.xRef].segment,
                                        ...segmentToAdd,
                                    ])),
                                sector:
                                    Array.from(new Set<string>([
                                        ...fareFamilyCollection[fareFamily.xRef].sector,
                                        ...sectorToAdd,
                                    ]))
                            };
                        }
                    }
                );
        }
        return fareFamilyCollection;
    }

    public computeSegmentDuration(sectors: ISector[]): IMinutesDuration {
        const minutes: number = sectors
            .reduce(
                (total: number, sector: ISector): number => {
                    return total + (sector.duration?.minutes ? sector.duration.minutes : 0) + (sector.durationStop ? sector.durationStop.minutes : 0);
                },
                0
            );
        return {minutes};
    }

    public extractCarrierCodesFromSectors(sectors: ISector[]): Array<{ code: string }> {
        const codes: Array<{ code: string }> = sectors.flatMap((sector: ISector): { code: string } => sector.carrier);
        return [
            ...new Map(
                codes.map((x: { code: string }): [string, { code: string }] => [x.code, x])
            ).values()
        ];
    }

    public getOfferAlternativeWithPriceXId(newGqa: ResponseForConvertOfferAfx2jsonType, offerAlternativePriceXId?: string, selectPrice: EXRateType = EXRateType.cost): OfferType {

        if (!offerAlternativePriceXId) {
            return newGqa.offer;
        }

        if (!newGqa.offer.offerAlternatives) {
            return newGqa.offer;
        }

        const offers: OfferType[] = [
            ...newGqa.offer.offerAlternatives.offerArray,
            newGqa.offer
        ];

        return offers.find(
            (alternative: OfferType): boolean => {
                return Boolean(
                    alternative.priceArray.find((price: PriceInOfferType): boolean => this.getCostOrRetail(price, selectPrice).xId === offerAlternativePriceXId)
                );
            });
    }

    public mapRootPrice(newGqa: ResponseForConvertOfferAfx2jsonType, offerAlternativePriceXId?: string, priceXReason: EUsedXReason = EUsedXReason.all): IPrice[] {
        const relatedOffer: OfferType = this.getOfferAlternativeWithPriceXId(newGqa, offerAlternativePriceXId);
        const price: IPrice[] = [];

        //total part
        const costTotal: PriceTotalsContentInBookingType = relatedOffer.total.cost;
        const retailTotal: PriceTotalsContentInBookingType = relatedOffer.total.retail;
        if (costTotal) {
            const costPrice: IPrice = {
                total: this.mapTotalPrice(costTotal, priceXReason),
                xType: EXRateType.cost,
            };
            price.push(costPrice);
        }
        if (retailTotal) {
            const retailPrice: IPrice = {
                total: this.mapTotalPrice(retailTotal, priceXReason),
                xType: EXRateType.retail,
            };
            price.push(retailPrice);
        }

        //detail part

        const detailsWithCost: PriceInOfferType[] = relatedOffer.priceArray
            .filter((priceItem: PriceInOfferType): boolean => Boolean(priceItem.cost));
        const detailsWithRetail: PriceInOfferType[] = relatedOffer.priceArray
            .filter((priceItem: PriceInOfferType): boolean => Boolean(priceItem.retail));

        if (detailsWithCost?.length) {
            const index: number = price.findIndex((priceItem: IPrice): boolean => priceItem.xType === EXRateType.cost);
            const costDetails: PriceEnvelopeInOfferType[] = detailsWithCost
                .map((priceItem: PriceInOfferType): PriceEnvelopeInOfferType => priceItem.cost);

            price[index].detail = this.buildPriceDetail(costDetails, priceXReason);

        }

        if (detailsWithRetail?.length) {
            const index: number = price.findIndex((priceItem: IPrice): boolean => priceItem.xType === EXRateType.retail);

            const retailDetails: PriceEnvelopeInOfferType[] = detailsWithRetail
                .map((priceItem: PriceInOfferType): PriceEnvelopeInOfferType => priceItem.retail);

            price[index].detail = this.buildPriceDetail(retailDetails, priceXReason);

        }

        return price;
    }

    public buildPriceDetail(details: PriceEnvelopeInOfferType[], priceXReason: EUsedXReason = EUsedXReason.all): IPriceDetails {
        const allDetailArray: PriceDetailInBookingType[] = [
            ...details
                .filter((item: PriceEnvelopeInOfferType): PriceDetailInBookingType[] => item.detailArray)
                .flatMap((item: PriceEnvelopeInOfferType): PriceDetailInBookingType[] => item.detailArray)
        ];
        const base: IPriceDetail[] = this.mapDetailArrayToPriceDetail(allDetailArray, 'base', priceXReason);
        const tax: IPriceDetail[] = this.mapDetailArrayToPriceDetail(allDetailArray, 'tax', priceXReason);

        const output: IPriceDetails = {
            base,
            tax,
        };
        if (allDetailArray.some((detail: PriceDetailInBookingType): boolean => Boolean(detail.feeArray))) {
            output.fee = this.mapDetailArrayToPriceDetail(allDetailArray, 'fee', priceXReason);
        }
        if (allDetailArray.some((detail: PriceDetailInBookingType): boolean => Boolean(detail.surchargeArray))) {
            output.surcharge = this.mapDetailArrayToPriceDetail(allDetailArray, 'surcharge', priceXReason);
        }
        return output;
    }

    public mapRootTotal(newGqa: ResponseForConvertOfferAfx2jsonType, offerAlternativePriceXId?: string, priceXReason: EUsedXReason = EUsedXReason.all): ITotal[] {
        const relatedOffer: OfferType = this.getOfferAlternativeWithPriceXId(newGqa, offerAlternativePriceXId);
        const total: ITotal[] = [];
        const totalCost: PriceTotalsContentInBookingType = relatedOffer.total.cost;
        const totalRetail: PriceTotalsContentInBookingType = relatedOffer.total.retail;

        if (totalCost) {
            total.push({
                ...this.mapPriceTotalValues(relatedOffer.total.cost.totalArray, priceXReason),
                xType: EXRateType.cost,
            });

        }
        if (totalRetail) {
            total.push({
                ...this.mapPriceTotalValues(relatedOffer.total.retail.totalArray, priceXReason),
                xType: EXRateType.retail
            });
        }
        return total;
    }

    public mapLuggages(newGqa: ResponseForConvertOfferAfx2jsonType, offerAlternativePriceXId?: string): IQuoteLuggage {
        const relatedOffer: OfferType = this.getOfferAlternativeWithPriceXId(newGqa, offerAlternativePriceXId);

        //it can have several prices in relatedOffer.priceArray
        //so we have to merge ruleArray from all price.cost.detailArray
        const ruleArray: RuleType[] = this.getAllRulesFromOffer(relatedOffer);
        if (ruleArray?.length && relatedOffer.originDestinationArray?.length) {
            const luggageRules: RuleType[] = ruleArray.filter((rule: RuleType): boolean => rule.xRole === "Luggage");
            if (luggageRules?.length) {
                const rule: IRule[] = luggageRules.map(
                    (luggageRule: RuleType): IRule => {
                        const segment: string[] = luggageRule.sectorArray
                            .map((sectorXRef: SectorXRefType): string => sectorXRef.xRef)
                            .map((xRef: string): OriginDestinationInOfferType => {
                                return (relatedOffer.originDestinationArray || [])
                                    .find((originDestination: OriginDestinationInOfferType): boolean => {
                                        return Boolean(originDestination.sectorArray
                                            .find((odSectorXref: SectorXRefType): boolean => odSectorXref.xRef === xRef));
                                    });
                            })
                            .map((originDestination: OriginDestinationInOfferType): string => originDestination.xRef);

                        const restriction: IRestriction[] = [];

                        if (luggageRule.luggage.quantity) {
                            restriction.push({
                                max: luggageRule.luggage.quantity.max.toString(),
                                unit: RestrictionUnit.luggage,
                                topic: 'quantity'
                            });
                        }
                        if (luggageRule.luggage.weight) {
                            restriction.push({
                                max: luggageRule.luggage.weight.max.toString(),
                                unit: luggageRule.luggage.weight.xUnit,
                                topic: 'weight'
                            });
                        }

                        return {
                            in: luggageRule.luggage.xCategory,
                            segment,
                            restriction,
                            traveller: luggageRule.travellerArray.map((travellerYoloXRef: TravellerXRefType): string => travellerYoloXRef.xRef)
                        };
                    })
                    .filter((ruleItem: IRule): boolean => Boolean(ruleItem.restriction?.length));
                return {
                    is: "Included",
                    rule
                };

            }
        }

        return null;
    }

    public mapTravellers(newGqa: ResponseForConvertOfferAfx2jsonType): ITravellers {
        const travellers: ITravellers = {};
        newGqa.travellerArray.forEach(
            (traveller: PersonNameType): void => {
                // @ts-ignore
                travellers[traveller.xId as string] = {
                    ...traveller,
                    // @ts-ignore
                    type: traveller.xType,
                };
            }
        );
        return travellers;
    }

    public mapTotalPrice(newGqaPrice: PriceTotalsContentInBookingType, priceXReason: EUsedXReason = EUsedXReason.all): IPriceTotal {
        return {
            base: this.mapPriceValues(newGqaPrice.baseArray, priceXReason),
            tax: this.mapPriceValues(newGqaPrice.taxArray, priceXReason),
            fee: this.mapPriceValues(newGqaPrice.feeArray, priceXReason),
        };
    }

    public mapDetailArrayToPriceDetail(detailArray: PriceDetailInBookingType[], target: string, priceXReason: EUsedXReason = EUsedXReason.all): IPriceDetail[] {
        return detailArray
            .map(
                (detail: PriceDetailInBookingType): IPriceDetail => {
                    return {
                        ...this.mapPriceValues(detail[`${target}Array`] as PriceItemType[], priceXReason),
                        traveller: (detail.travellerArray || []).map((travellerItem: TravellerXRefType): {
                            xId: string
                        } => ({xId: travellerItem.xRef})),
                    };
                }
            );
    }

    public mapPriceTotalValues(prices: PriceItemType[], priceXReason: EUsedXReason = EUsedXReason.all): ITotal {
        if (prices?.length) {
            const noReasonPrice: PriceItemType = prices
                .find((priceItem: PriceItemType): boolean => !priceItem.xReason);

            const servicePrice: PriceItemType = prices
                .find((priceItem: PriceItemType): boolean => priceItem.xReason === "Service");
            const transportationPrice: PriceItemType = prices
                .find((priceItem: PriceItemType): boolean => priceItem.xReason === "Transportation");
            const paymentPrice: PriceItemType = prices
                .find((priceItem: PriceItemType): boolean => priceItem.xReason === "Payment");

            // /!\ This could evolve in near future
            // For now, if price have no xReason, it is the global total
            let value: number = 0;
            switch (priceXReason) {
                // we need price without service #35230
                case EUsedXReason.TransportationPlusPayment:
                    value = (transportationPrice ? transportationPrice.currencyArray[0].value : 0) +
                        (paymentPrice ? paymentPrice.currencyArray[0].value : 0);
                    break;
                case EUsedXReason.Service:
                    value = servicePrice ? servicePrice.currencyArray[0].value : 0;
                    break;
                case EUsedXReason.all:
                default:
                    value = noReasonPrice.currencyArray[0].value;
                    break;
            }
            const amount: number = value / 100;
            const currency: string = prices[0].currencyArray[0].currencyCode;
            const decimals: number = prices[0].currencyArray[0].decimals;

            return {
                value,
                amount,
                currency,
                decimals,
            };
        }
        return null;
    }

    public mapPriceValues(prices: PriceItemType[], priceXReason: EUsedXReason = EUsedXReason.all): ITotal {
        if (prices?.length && prices[0].currencyArray?.length) {
            // EJ - if price have no currency object, ignore it https://projets2.afidium.com/issues/34126
            // /!\ assume all the same currency at this point
            const value: number = prices
                .filter((price: PriceItemType): boolean => {
                    switch (priceXReason) {
                        // we need price without service #35230
                        case EUsedXReason.TransportationPlusPayment:
                            return price.xReason === 'Transportation' || price.xReason === 'Payment';
                        case EUsedXReason.Service:
                            return price.xReason === 'Service';
                        case EUsedXReason.all:
                        default:
                            return Boolean(price.currencyArray?.length);
                    }
                })
                .map((price: PriceItemType): number => price.currencyArray[0].value)
                .reduce((a: number, b: number): number => a + b, 0);
            const amount: number = value / 100;
            const currency: string = prices[0].currencyArray[0].currencyCode;
            const decimals: number = prices[0].currencyArray[0].decimals;

            return {
                value,
                amount,
                currency,
                decimals,
            };
        }
        return null;
    }

    public mapXrefToStringArray(xRefArray: PropertyXRefType[]): string[] {
        return xRefArray.map((xRefItem: PropertyXRefType): string => xRefItem.xRef);
    }

    public getSectorWithXref(xRef: string, newGqa: ResponseForConvertOfferAfx2jsonType): SectorInTripType {
        return newGqa.trip.sectorArray
            .find((sector: SectorInTripType): boolean => sector.xId === xRef);
    }

    public getJourneyWithXref(xRef: string, newGqa: ResponseForConvertOfferAfx2jsonType): JourneyInTripType {
        return newGqa.trip.journeyArray
            .find((journey: JourneyInTripType): boolean => journey.xId === xRef);
    }

    public getOriginDestinationWithXref(xRef: string, newGqa: ResponseForConvertOfferAfx2jsonType): OriginDestinationInTripType {
        return newGqa.trip.originDestinationArray
            .find((originDestination: OriginDestinationInTripType): boolean => originDestination.xId === xRef);
    }

    public getTransportationWithXref(xRef: string, newGqa: ResponseForConvertOfferAfx2jsonType): TransportationInTripType {
        return newGqa.trip.transportationArray
            .find((transportation: TransportationInTripType): boolean => transportation.xId === xRef);
    }

    public getAirportCodeFromAfid(afid: string, newGqa: ResponseForConvertOfferAfx2jsonType): string {
        const location: LocationForAirType = newGqa.dictionary.locationArray
            .find((locationItem: LocationForAirType): boolean => locationItem.afid === afid);
        if (location) {
            return newGqa.dictionary.airportArray
                .find((airportItem: Airport): boolean => airportItem.afid === location.airport.afid)
                .iata;
        }
        return afid ? afid.split('_')[1] : null;
    }

    public getCarrierCodeFromAfid(afid: string, newGqa: ResponseForConvertOfferAfx2jsonType): string {
        return newGqa.dictionary.carrierArray
            .find((carrierItem: CarrierType): boolean => carrierItem.afid === afid)
            .iata;
    }

    // If sector aircraft afid is provided, search for name or iata code in dictionary
    public getAircraftCodeFromAfid(afid: string, newGqa: ResponseForConvertOfferAfx2jsonType): string {
        if (newGqa.dictionary.aircraftArray?.length) {
            const aircraft: AircraftType = newGqa.dictionary.aircraftArray
                .find((aircraftItem: AircraftType): boolean => aircraftItem.afid === afid);
            return aircraft?.iata !== null ? aircraft?.iata : null;
        }
        return null;
    }

    private getAllRulesFromOffer(relatedOffer: OfferType): RuleType[] {
        const rates: RateInBookingType[] = this.getAllRatesFromOffer(relatedOffer)
            .filter((rateItem: RateInBookingType): boolean => Boolean(rateItem.ruleArray?.length));
        if (rates?.length) {
            return rates
                .flatMap((rateItem: RateInBookingType): RuleType[] => rateItem.ruleArray);
        }
        return null;
    }

    private getAllFareComponentArrayFromOffer(relatedOffer: OfferType): FareComponentInBookingType[] {
        const rates: RateInBookingType[] = this.getAllRatesFromOffer(relatedOffer)
            .filter((rateItem: RateInBookingType): boolean => Boolean(rateItem.fareComponentArray?.length));
        if (rates?.length) {
            return rates
                .flatMap((rateItem: RateInBookingType): FareComponentInBookingType[] => rateItem.fareComponentArray);
        }
        return null;
    }

    private getAllRatesFromOffer(relatedOffer: OfferType): RateInBookingType[] {

        return relatedOffer.priceArray
            .filter((priceItem: PriceInOfferType): boolean => {
                return Boolean(priceItem.cost) &&
                    priceItem.cost.xRole === 'Transportation' &&
                    Boolean(priceItem.cost.detailArray?.length) &&
                    Boolean(priceItem.cost.detailArray[0].rate);
            })
            .map((priceItem: PriceInOfferType): RateInBookingType => priceItem.cost.detailArray[0].rate);
    }

    private getCostOrRetail(item: any, selectedPrice: EXRateType): any {
        return (item && item.retail && selectedPrice === EXRateType.retail) || (item.retail && !item.cost) ? item.retail : item.cost;
    }
}
